import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Flags, SegFlags, translate } from "../../common/providers";
import { exportView } from "../../navigation";
import { ChildrenRequirementDetailsContainer, DeliverablesList, icon, IconComponent } from "../../common/components";
import styles from "./ChildrenRequirements.module.css";
import { Filters } from "./components";
import { isNonEmptyObject } from "../../common/utils";
import { useApi } from "../../common/hooks";
import { ParagraphOutService } from "../../api";

const ChildrenRequirements = () => {
	const { call: getAllCategoriesForParagraphOut } = useApi(ParagraphOutService.getAllCategoriesForParagraphOut);
	const { call: updateCategoriesForParagraphOut } = useApi(ParagraphOutService.updateCategoriesForParagraphOut);
	const { call: getParagraphOutParentDetails } = useApi(ParagraphOutService.getParagraphOutParentDetails);
	const projectId = useSelector(({ context }) => context.project?.id);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const [filters, setFilters] = useState({});
	const [selection, setSelection] = useState(null);
	const [updatedChildrenRequirement, setUpdatedChildrenRequirement] = useState(null);
	const [parentRequirement, setParentRequirement] = useState(null);
	const [toUpdate, setToUpdate] = useState({});

	const getAttributes = useCallback(() => {
		getAllCategoriesForParagraphOut(
			{
				projectId,
				paragraphOutId: selection.requirement?.id,
			},
			{ search: "setup/_setup_THEMES" }
		)
			.then((data) => {
				setUpdatedChildrenRequirement((prevState) => ({
					...prevState,
					themes: data,
				}));
			})
			.catch(console.error)
			.finally(() => {
				getAllCategoriesForParagraphOut(
					{
						projectId,
						paragraphOutId: selection.requirement?.id,
					},
					{ search: "setup/_setup_PHASE" }
				)
					.then((data) => {
						setUpdatedChildrenRequirement((prevState) => ({
							...prevState,
							phaseValidation: data,
						}));
					})
					.catch(console.error);
			})
			.finally(() => {
				getAllCategoriesForParagraphOut(
					{
						projectId,
						paragraphOutId: selection.requirement?.id,
					},
					{ search: "setup/_setup_LOCALISATION" }
				)
					.then((data) => {
						setUpdatedChildrenRequirement((prevState) => ({
							...prevState,
							locations: data,
						}));
					})
					.catch(console.error);
			})
			.finally(() => {
				getAllCategoriesForParagraphOut(
					{
						projectId,
						paragraphOutId: selection.requirement?.id,
					},
					{ search: "setup/_setup_METHOD" }
				)
					.then((data) => {
						setUpdatedChildrenRequirement((prevState) => ({
							...prevState,
							methodValidation: data,
						}));
					})
					.catch(console.error);
			});
	}, [getAllCategoriesForParagraphOut, selection, projectId]);
	useEffect(() => {
		if (selection?.requirement && selection?.requirement?.informationId && projectId) {
			getAttributes();
		}
	}, [getAttributes, selection, projectId]);
	useEffect(() => {
		if (isNonEmptyObject(selection?.requirement)) {
			setUpdatedChildrenRequirement(selection.requirement);
		}
	}, [selection]);
	useEffect(() => {
		const documentTitle = translate("children-requirements-title");
		document.title = documentTitle;
	}, []);
	useEffect(() => {
		if (projectId && selection?.requirement?.informationId) {
			getParagraphOutParentDetails({ projectId, requirementId: selection.requirement.informationId })
				.then(setParentRequirement)
				.catch(console.error);
		}
	}, [getParagraphOutParentDetails, projectId, selection]);
	const handleChangeFilters = (newFilters) => {
		setFilters(newFilters);
	};
	const handleSelect = (newSelection) => {
		setSelection(newSelection);
	};
	const handleUpdatedChildrenRequirement = (identifier, toAdd, toDelete) => {
		setToUpdate((prev) => ({ ...prev, [identifier]: { toAdd, toDelete } }));
	};
	const handleCancelEdit = () => {
		setToUpdate({});
	};

	const handleSaveEdit = (onCallBack) => {
		let addThematic = [];
		let deleteThematic = [];
		Object.keys(toUpdate)?.forEach((key) => {
			addThematic = [...addThematic, ...toUpdate[key].toAdd];
			deleteThematic = [...deleteThematic, ...toUpdate[key].toDelete];
		});
		updateCategoriesForParagraphOut(
			{ projectId, paragraphOutId: selection.requirement?.id },
			{ addThematic, deleteThematic }
		)
			.then(() => {
				getAttributes();
				onCallBack();
			})
			.catch(console.error);
	};
	return (
		<>
			<div className={styles.container} data-caution-banner-disabled={cautionBannerDisabled}>
				<div className={styles.container__leftPanel}>
					<Filters filters={filters} onChangeFilters={handleChangeFilters} />
					<div className={styles.deliverablesListContainer}>
						<DeliverablesList isGlobalDeliverables filters={filters} onSelect={handleSelect} />
					</div>
				</div>
				<div className={styles.container__rightPanel}>
					{(selection && (
						<ChildrenRequirementDetailsContainer
							deliverableName={selection.deliverableName}
							isPhaseLocked={selection.phaseLocked}
							parentRequirement={parentRequirement}
							updatedChildrenRequirement={updatedChildrenRequirement}
							onCancelEdit={handleCancelEdit}
							onSaveEdit={handleSaveEdit}
							onUpdateChildrenRequirement={handleUpdatedChildrenRequirement}
						/>
					)) || (
						<div className={styles.emptyContainer}>
							<span>
								<IconComponent color="var(--blue-500)" icon={icon.faInfoCircle} />
							</span>
							<span>{translate("children-requirements.empty-state")}</span>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default exportView({
	path: "/projects/:projectId/review/children-requirements",
	localesPath: "/children-requirements/locales",
	component: ChildrenRequirements,
	flag: Flags.COVERAGE_PLUGIN,
	segFlag: SegFlags.DELIVERABLES,
});
